import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    MdArrowBack, MdAdd, MdDelete, MdEdit, MdSave, MdCancel,
    MdDownload, MdTableChart, MdToggleOn, MdToggleOff,
    MdCheckCircle, MdEditNote
} from 'react-icons/md';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import { useLanguage } from '../../context/LanguageContext';

export default function AdminManualDataPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [company, setCompany] = useState(null);
    const [config, setConfig] = useState({ enabled: false, fields: [] });
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [newField, setNewField] = useState('');
    const [addingField, setAddingField] = useState(false);
    const [renaming, setRenaming] = useState(null); // { fieldName, newName }
    const [deletingField, setDeletingField] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [compRes, entriesRes] = await Promise.all([
                api.get(`/companies/${id}`),
                api.get(`/manual-data/${id}`)
            ]);
            const comp = compRes.data.data;
            setCompany(comp);
            const cfg = comp?.manualDataConfig;
            setConfig({
                enabled: cfg?.enabled ?? false,
                fields: Array.isArray(cfg?.fields) ? cfg.fields : []
            });
            setEntries(Array.isArray(entriesRes.data.data) ? entriesRes.data.data : []);
        } catch {
            toast.error(t('manualData.dataLoadFailed'));
        } finally {
            setLoading(false);
        }
    }, [id, t]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleToggle = async () => {
        try {
            setSaving(true);
            const newEnabled = !config.enabled;
            const res = await api.put(`/manual-data/${id}/config`, { enabled: newEnabled });
            setConfig(c => ({ ...c, enabled: res.data.data?.enabled ?? newEnabled }));
            toast.success(newEnabled ? t('companyDetail.manualDataEnabled') : t('companyDetail.manualDataDisabled'));
        } catch {
            toast.error(t('companyDetail.settingFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleAddField = async () => {
        const trimmed = newField.trim();
        if (!trimmed) return toast.error(t('manualData.paramRequired'));
        if (config.fields.includes(trimmed)) return toast.error(t('manualData.paramExists'));
        try {
            setAddingField(true);
            const updatedFields = [...config.fields, trimmed];
            const res = await api.put(`/manual-data/${id}/config`, { fields: updatedFields });
            setConfig(c => ({ ...c, fields: res.data.data?.fields || updatedFields }));
            setNewField('');
            toast.success(t('manualData.paramAdded'));
        } catch {
            toast.error(t('manualData.paramAddFailed'));
        } finally {
            setAddingField(false);
        }
    };

    const handleDeleteField = async (fieldName) => {
        if (!window.confirm(t('manualData.deleteParamConfirm', { name: fieldName }))) return;
        try {
            setDeletingField(fieldName);
            await api.delete(`/manual-data/${id}/field/${encodeURIComponent(fieldName)}`);
            toast.success(t('manualData.paramDeleted'));
            fetchData();
        } catch {
            toast.error(t('manualData.paramDeleteFailed'));
        } finally {
            setDeletingField(null);
        }
    };

    const handleRename = async () => {
        const newName = renaming?.newName?.trim();
        if (!newName) return toast.error(t('manualData.newNameRequired'));
        if (newName === renaming.fieldName) { setRenaming(null); return; }
        try {
            await api.put(`/manual-data/${id}/field/${encodeURIComponent(renaming.fieldName)}/rename`, { newName });
            toast.success(t('manualData.paramRenamed'));
            setRenaming(null);
            fetchData();
        } catch {
            toast.error(t('manualData.paramRenameFailed'));
        }
    };

    const handleDownload = () => {
        if (entries.length === 0) return toast.error(t('manualData.noDownloadData'));

        // Pivot: her parametre ayrı sütun, her tarih ayrı satır
        const fields = config.fields;
        const allDates = [...new Set(entries.map(e => e.date))].sort((a, b) => b.localeCompare(a));

        // { date -> { fieldName -> value } }
        const byDate = {};
        entries.forEach(e => {
            if (!byDate[e.date]) byDate[e.date] = {};
            byDate[e.date][e.fieldName] = e.value;
        });

        const rows = allDates.map(date => {
            const row = {};
            row[t('common.date')] = date;
            fields.forEach(f => { row[f] = byDate[date]?.[f] ?? ''; });
            return row;
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, t('manualData.title'));
        XLSX.writeFile(wb, `${company?.name || 'firma'}_${t('manualData.title').toLowerCase().replace(/\s+/g, '_')}.xlsx`);
    };

    // Group entries by fieldName
    const grouped = {};
    config.fields.forEach(f => { grouped[f] = []; });
    entries.forEach(e => {
        if (!grouped[e.fieldName]) grouped[e.fieldName] = [];
        grouped[e.fieldName].push(e);
    });

    if (loading) return <div className="loading-page"><div className="spinner" style={{ width: 48, height: 48 }} /></div>;

    return (
        <div className="page-wrapper">
            {/* ── Header ── */}
            <div className="page-header" style={{ marginBottom: 32, alignItems: 'center' }}>
                <div className="page-header-left" style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <button className="btn btn-secondary btn-icon" onClick={() => navigate(`/admin/companies/${id}`)} id="back-btn">
                        <MdArrowBack />
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                        <div style={{ padding: 12, borderRadius: 12, background: 'rgba(79,142,247,0.12)', color: 'var(--primary)' }}>
                            <MdEditNote size={26} />
                        </div>
                        <div>
                            <h1 className="page-title" style={{ margin: 0, fontSize: 24 }}>{t('manualData.title')}</h1>
                            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
                                {t('manualData.subtitle', { company: company?.name })}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="page-header-actions">
                    {entries.length > 0 && (
                        <button className="btn btn-secondary btn-sm" onClick={handleDownload} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MdDownload /> {t('sensorDetail.exportExcel')}
                        </button>
                    )}
                    {/* Toggle */}
                    <button
                        onClick={handleToggle}
                        disabled={saving}
                        id="toggle-manual-data-btn"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 20px', borderRadius: 10, border: 'none', cursor: 'pointer',
                            fontWeight: 700, fontSize: 14, fontFamily: 'Inter, sans-serif',
                            background: config.enabled ? 'rgba(34,197,94,0.12)' : 'var(--bg-elevated)',
                            color: config.enabled ? 'var(--success)' : 'var(--text-muted)',
                            transition: 'all 0.2s'
                        }}
                    >
                        {config.enabled ? <><MdToggleOn size={22} /> {t('common.active')}</> : <><MdToggleOff size={22} /> {t('common.passive')}</>}
                    </button>
                </div>
            </div>

            {/* ── Status Banner ── */}
            <div style={{
                padding: '14px 20px', borderRadius: 12, marginBottom: 28,
                background: config.enabled ? 'rgba(34,197,94,0.07)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${config.enabled ? 'rgba(34,197,94,0.25)' : 'var(--border)'}`,
                fontSize: 13, color: config.enabled ? 'var(--success)' : 'var(--text-muted)',
                display: 'flex', alignItems: 'center', gap: 8
            }}>
                {config.enabled ? (
                    <>
                        <MdCheckCircle size={16} />
                        <span dangerouslySetInnerHTML={{ __html: t('manualData.enabledBanner') }} />
                    </>
                ) : (
                    <>
                        <span dangerouslySetInnerHTML={{ __html: t('manualData.disabledBanner') }} />
                    </>
                )}
            </div>

            <div className="detail-grid-2col detail-grid-2col--wide" style={{ alignItems: 'start' }}>

                {/* ── Left: Field Management ── */}
                <motion.div initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}>
                    <div className="card" style={{ padding: 24 }}>
                        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18, display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}>
                            <MdTableChart style={{ color: 'var(--primary)' }} /> {t('manualData.parameters')}
                        </div>

                        {/* Add parameter */}
                        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                            <input
                                className="form-input"
                                placeholder={t('manualData.newParamPlaceholder')}
                                value={newField}
                                onChange={e => setNewField(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') handleAddField(); }}
                                id="new-manual-field-input"
                                style={{ flex: 1 }}
                            />
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={handleAddField}
                                disabled={addingField || !newField.trim()}
                                id="add-manual-field-btn"
                            >
                                {addingField ? <span className="spinner spinner-sm" /> : <MdAdd />}
                            </button>
                        </div>

                        {config.fields.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-muted)', fontSize: 13 }}>
                                {t('manualData.noParams')}
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {config.fields.map(field => (
                                    <motion.div
                                        key={field}
                                        layout
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: 10,
                                            padding: '12px 14px', borderRadius: 10,
                                            background: 'var(--bg-body)', border: '1px solid var(--border)'
                                        }}
                                    >
                                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--primary)', flexShrink: 0 }} />

                                        {renaming?.fieldName === field ? (
                                            <>
                                                <input
                                                    className="form-input form-input-sm"
                                                    value={renaming.newName}
                                                    onChange={e => setRenaming(r => ({ ...r, newName: e.target.value }))}
                                                    onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setRenaming(null); }}
                                                    autoFocus style={{ flex: 1 }}
                                                />
                                                <button className="btn btn-primary btn-sm btn-icon" onClick={handleRename} style={{ width: 30, height: 30 }}><MdSave /></button>
                                                <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setRenaming(null)} style={{ width: 30, height: 30 }}><MdCancel /></button>
                                            </>
                                        ) : (
                                            <>
                                                <span style={{ flex: 1, fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{field}</span>
                                                <span style={{ fontSize: 11, color: 'var(--text-muted)', marginRight: 4 }}>
                                                    {(grouped[field] || []).length} {t('common.records')}
                                                </span>
                                                <button className="btn btn-secondary btn-sm btn-icon" onClick={() => setRenaming({ fieldName: field, newName: field })} style={{ width: 28, height: 28 }} title={t('common.rename')}><MdEdit /></button>
                                                <button className="btn btn-danger btn-sm btn-icon" onClick={() => handleDeleteField(field)} disabled={deletingField === field} style={{ width: 28, height: 28 }} title={t('common.delete')}>
                                                    {deletingField === field ? <span className="spinner spinner-sm" /> : <MdDelete />}
                                                </button>
                                            </>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        )}
                    </div>
                </motion.div>

                {/* ── Right: Data Tables per field ── */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                    {config.fields.length === 0 ? (
                        <div className="card" style={{ padding: 40, textAlign: 'center' }}>
                            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                            <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--text-primary)', marginBottom: 6 }}>{t('manualData.noParamsTitle')}</div>
                            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{t('manualData.noParamsDesc')}</div>
                        </div>
                    ) : (
                        config.fields.map((field, fi) => {
                            const fieldEntries = (grouped[field] || []).sort((a, b) => b.date.localeCompare(a.date));
                            return (
                                <motion.div
                                    key={field}
                                    className="card"
                                    style={{ padding: 0, overflow: 'hidden' }}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: fi * 0.06 }}
                                >
                                    <div style={{
                                        padding: '16px 24px', borderBottom: '1px solid var(--border)',
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        background: 'rgba(79,142,247,0.04)'
                                    }}>
                                        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <MdTableChart /> {field}
                                        </div>
                                        <span style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{fieldEntries.length} {t('common.records')}</span>
                                    </div>

                                    {fieldEntries.length === 0 ? (
                                        <div style={{ padding: '28px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                                            {t('manualData.noDataYet')}
                                        </div>
                                    ) : (
                                        <div style={{ maxHeight: 300, overflowY: 'auto', overflowX: 'auto' }}>
                                            <table className="table" style={{ margin: 0 }}>
                                                <thead>
                                                    <tr>
                                                        <th>{t('common.date')}</th>
                                                        <th>{t('manualData.parameterLabel')}</th>
                                                        <th>{t('common.value')}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {fieldEntries.map(e => (
                                                        <tr key={e._id}>
                                                            <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{e.date}</td>
                                                            <td style={{ fontWeight: 600 }}>{e.fieldName}</td>
                                                            <td style={{ fontWeight: 700, color: 'var(--success)', fontFamily: 'monospace' }}>{e.value}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
